/**
 * AI Assistant API Service
 * Handles all API calls for the AI chatbot assistant
 * Phase 9 Implementation
 */

import { apiClient } from './apiClient';
import { ApiResponse } from '../types';

// ===== Types =====

export interface LeadPreview {
  id: string;
  contactName: string;
  companyName: string;
  status: string;
  priority: string;
  aiScore?: number;
}

export interface TaskPreview {
  id: string;
  title: string;
  taskType: string;
  priority: string;
  dueDate?: string;
}

export interface ActionableIntent {
  actionType: 'ASSIGN_LEADS' | 'CREATE_TASK' | 'ASSIGN_TASKS';
  params: {
    leadIds?: string[];
    taskIds?: string[];
    assigneeId?: string;
    assigneeName?: string;
    title?: string;
    taskType?: string;
    dueDate?: string;
    priority?: string;
  };
  requiresConfirmation: boolean;
  previewData?: LeadPreview[] | TaskPreview[];
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  message: string;
  response: string;
  intent: string;
  suggestions: string[];
  createdAt: string;
  actionableIntent?: ActionableIntent | null;
}

export interface ConversationSummary {
  conversationId: string;
  title: string;
  lastMessage: string;
  createdAt: string;
}

export interface ConversationHistory {
  conversationId: string;
  messages: {
    id: string;
    userMessage: string;
    aiResponse: string;
    intent: string;
    timestamp: string;
  }[];
}

export interface QuickActionResult {
  type: string;
  data: any;
}

export interface ChatInput {
  message: string;
  conversationId?: string;
  context?: Record<string, any>;
}

export interface ConfirmActionInput {
  actionType: string;
  params: Record<string, any>;
}

export interface ConfirmActionResult {
  message: string;
  data: any;
}

// ===== API Functions =====

export const aiAssistantApi = {
  /**
   * Send a message to the AI assistant
   */
  chat: async (input: ChatInput): Promise<ChatMessage> => {
    const response = await apiClient.post<ApiResponse<ChatMessage>>(
      '/ai-assistant/chat',
      input
    );
    return response.data.data;
  },

  /**
   * Get all conversations for the current user
   */
  getConversations: async (): Promise<ConversationSummary[]> => {
    const response = await apiClient.get<ApiResponse<ConversationSummary[]>>(
      '/ai-assistant/conversations'
    );
    return response.data.data;
  },

  /**
   * Get conversation history by ID
   */
  getConversationHistory: async (conversationId: string): Promise<ConversationHistory> => {
    const response = await apiClient.get<ApiResponse<ConversationHistory>>(
      `/ai-assistant/conversations/${conversationId}`
    );
    return response.data.data;
  },

  /**
   * Delete a conversation
   */
  deleteConversation: async (conversationId: string): Promise<void> => {
    await apiClient.delete(`/ai-assistant/conversations/${conversationId}`);
  },

  /**
   * Execute a quick action
   */
  executeQuickAction: async (
    action: string,
    params?: Record<string, any>
  ): Promise<QuickActionResult> => {
    const response = await apiClient.post<ApiResponse<QuickActionResult>>(
      '/ai-assistant/quick-action',
      { action, params }
    );
    return response.data.data;
  },

  /**
   * Confirm and execute an actionable intent
   */
  confirmAction: async (input: ConfirmActionInput): Promise<ConfirmActionResult> => {
    const response = await apiClient.post<ApiResponse<ConfirmActionResult>>(
      '/ai-assistant/confirm-action',
      input
    );
    return response.data.data;
  },

  /**
   * Get team members for assignment selection
   */
  getTeamMembers: async (): Promise<TeamMember[]> => {
    const response = await apiClient.get<ApiResponse<TeamMember[]>>(
      '/ai-assistant/team-members'
    );
    return response.data.data;
  }
};

export default aiAssistantApi;
